import React from 'react';
import { Typography, Box, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { StepButtonBar } from '../EditLegalListWizard/StepButtonBar';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { StepProps } from './types';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import UpdateConnectionInfo from './UpdateConnectionInfo';

const PRIMARY = '#c4122f';
const GREEN = '#4caf50';

interface Step7LawExistingCompanyUnitPreviewProps extends StepProps {
  selectedLaw?: { id: number; name: string; number: string } | null;
  selectedCompanyUnit?: SelectOption | null;
  selectedConnectionType?: SelectOption | null;
  selectedAction?: SelectOption | null;
  localAddedItems?: any[];
  localDeletedItems?: any[];
  onSave: () => void;
  isSaving?: boolean;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

const Step7LawExistingCompanyUnitPreview = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  selectedLaw,
  selectedCompanyUnit,
  selectedConnectionType,
  selectedAction,
  localAddedItems = [],
  localDeletedItems = [],
  onSave,
  isSaving = false,
  currentStepName,
    updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step7LawExistingCompanyUnitPreviewProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(2) : theme.spacing(3);

  const getActionLabel = () => {
    return selectedAction?.value === 'add'
      ? formatMessage({ id: 'form.add', defaultMessage: 'Add' })
      : formatMessage({ id: 'form.delete', defaultMessage: 'Delete' });
  };

  const getConnectionTypeLabel = () => {
    return selectedConnectionType?.label || '';
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          width: '100%',
          padding: paperPadding,
          backgroundColor: '#f7f0f1',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >

        {/* Law Section */}
        <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: theme.spacing(2) }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
            {formatMessage({ id: 'law-portal.manage.law-list.table.law' })}
          </Typography>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 14px',
              backgroundColor: '#fff',
              borderRadius: '4px',
            }}
          >
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                backgroundColor: '#e3f2fd',
                borderRadius: '6px',
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon style={{ color: '#1976d2', fontSize: '24px' }} />
            </Box>
            <Box>
              <Typography variant="body2" style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}>
                {selectedLaw?.number} – {selectedLaw?.name}
              </Typography>
              <Typography variant="caption" style={{ color: '#666' }}>
                {formatMessage({ id: 'form.compliance.selectCompanyUnits' })}: {selectedCompanyUnit?.label}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Connection Type Section */}
        <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: theme.spacing(2) }}>
          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
            {formatMessage({ id: 'law-portal.manage.update-laws.type' })}
          </Typography>
          <Typography variant="body2" style={{ color: '#222', padding: '12px 14px', backgroundColor: '#fff', borderRadius: '4px' }}>
            {getConnectionTypeLabel()}
          </Typography>
        </Box>


        {/* Added Items Section */}
        {selectedAction?.value === 'add' && localAddedItems.length > 0 && (
          <Box style={{ display: 'flex', flexDirection: 'column', marginBottom: theme.spacing(2) }}>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'form.add', defaultMessage: 'Add' })} ({localAddedItems.length})
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              {localAddedItems.map((item, idx) => (
                <Box
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 14px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    marginBottom: idx < localAddedItems.length - 1 ? theme.spacing(0.5) : 0,
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      backgroundColor: GREEN,
                      borderRadius: '6px',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionOutlinedIcon style={{ color: '#fff', fontSize: '24px' }} />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}>
                    {item.name || item.label || JSON.stringify(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Deleted Items Section */}
        {selectedAction?.value === 'delete' && localDeletedItems.length > 0 && (
          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: theme.spacing(1) }}>
              {formatMessage({ id: 'form.delete', defaultMessage: 'Delete' })} ({localDeletedItems.length})
            </Typography>
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              {localDeletedItems.map((item, idx) => (
                <Box
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 14px',
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    marginBottom: idx < localDeletedItems.length - 1 ? theme.spacing(0.5) : 0,
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      backgroundColor: PRIMARY,
                      borderRadius: '6px',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionOutlinedIcon style={{ color: '#fff', fontSize: '24px' }} />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 500, fontSize: '14px', color: '#222' }}>
                    {item.name || item.label || JSON.stringify(item)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </div>
      {/* <UpdateConnectionInfo
      updateConnectionInfo={updateConnectionInfo}
      currentStepName={currentStepName}
      onUpdate={setUpdateConnectionInfo}
    /> */}

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onSave}
          nextLabel="form.save"
          nextDisabled={isSaving}
          isLoading={isSaving}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />       
      </Box>
    </div>
  );
};

export default Step7LawExistingCompanyUnitPreview;
